import React, { useState, useRef, useEffect } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

interface ImagePair {
    left_id: number;
    right_id: number;
    leftImage: string;
    rightImage: string;
}



const MatchAnimal: React.FC = () => {
    const [connections, setConnections] = useState<{ x1: number; y1: number; x2: number; y2: number; correct: boolean }[]>([]);
    const [selectedLeft, setSelectedLeft] = useState<{ id: number; x: number; y: number } | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [totalQuestion, setTotalQuestion] = useState<any>([]);
    const [showImage, setShowImage] = useState(false);
    const [question, setQuestion] = useState('');
    const [questions, setQuestions] = useState<ImagePair[]>([]);
    const [height, setHeight] = useState('0px');
    const modal = useRef<HTMLIonModalElement>(null);

    // Get element position relative to .match-container
    const getRelativePosition = (element: HTMLDivElement) => {
        const container = containerRef.current; console.log(containerRef)
        if (!container) return { x: 0, y: 0 };

        const elementRect = element.getBoundingClientRect(); setHeight((container.scrollHeight) + 'px')
        const containerRect = container.getBoundingClientRect();

        const screenWidth = window.innerWidth;
        let elementX, elementY;
        if (screenWidth >= 1024) {
            elementX = element.offsetLeft + elementRect.width;
            elementY = element.offsetTop + elementRect.height / 2;
        }
        else {
            elementX = element.offsetLeft + elementRect.width / 2;
            elementY = element.offsetTop + elementRect.height / 2;
        }

        return {
            x: elementX,
            y: elementY
        }
    };

    // Handle left image click
    const handleLeftClick = (event: React.MouseEvent<HTMLDivElement>, id: number) => {
        const position = getRelativePosition(event.currentTarget); console.log('left position - ', position)
        setSelectedLeft({ id, x: position.x, y: position.y });
    };

    // Handle right image click and draw SVG line
    const handleRightClick = (event: React.MouseEvent<HTMLDivElement>, rightId: number) => {
        if (selectedLeft) {
            const position = getRelativePosition(event.currentTarget); console.log('right position - ', position)

            // Check if the connection is correct
            const isCorrect = questions.some(pair => selectedLeft.id === rightId);

            setConnections([...connections, { x1: selectedLeft.x, y1: selectedLeft.y, x2: position.x, y2: position.y, correct: isCorrect }]);
            setSelectedLeft(null);
        }
    };

    // Reset connections
    const resetConnections = () => {
        setConnections([]);
        setSelectedLeft(null);
    };

    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            // Generate a random index
            const randomIndex = Math.floor(Math.random() * (i + 1));
            // Swap elements
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    const fetchQuestions = async () => {
        try {
            let data = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'MatchAnimal.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/MatchAnimal.json');
                data = module.default;
            }

            const shuffledData = shuffleArray(data);
            setTotalQuestion(shuffledData);
            setQuestions(shuffledData[currentIndex].pairs);
            setQuestion(shuffledData[currentIndex].question);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };


    useEffect(() => {
        fetchQuestions();
        document.title = "Match Animals with Their Images | UKG General Awareness Worksheet"; // Set page title
    }, []);

    const nextQuestion = () => {
        console.log('next question')
        resetConnections()
        setShowImage(true);
        setTimeout(() => {
            if (currentIndex != totalQuestion.length - 1) {
                setCurrentIndex(currentIndex + 1);
                console.log(totalQuestion[currentIndex + 1].pairs)
                setQuestions(totalQuestion[currentIndex + 1].pairs)
            }
            else {
                setCurrentIndex(0);
                fetchQuestions();
            }
            setShowImage(false);
        }, 3000);
    }

    return (
        <>
            <Helmet>
                <meta name="description" content="Help UKG kids learn animal names and appearances by matching animals with their correct images. Fun, engaging, and ideal for early learners." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/general-awareness/animals/match-images" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Match the Animals</h1>

                                <p>
                                    Make animal learning fun with this interactive <strong>UKG-level image matching worksheet</strong>. Children will match the names of animals with their corresponding pictures, helping them to develop recognition, vocabulary, and cognitive skills.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Recognize animals by their appearance</li>
                                    <li>Improve visual memory and word association</li>
                                    <li>Strengthen early science and general awareness concepts</li>
                                </ul>

                                <h2>🧠 What&apos;s Included?</h2>
                                <p>
                                    The worksheet features colorful images of common animals like <em>elephant, lion, cat, dog, cow, monkey, tiger, and zebra</em>. Students will match each picture with the correct animal name from a given list.
                                </p>


                                <h2>🧩 Example Activity</h2>
                                <ul>
                                    <li>Image of 🐘 → Elephant</li>
                                    <li>Image of 🐯 → Tiger</li>
                                    <li>Image of 🐶 → Dog</li>
                                </ul>

                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Sense Organs</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-home"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My Home</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-school"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My School</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>👩‍🏫 Teaching Tips</h2>
                                <ul>
                                    <li>Use flashcards or real-life toys for reinforcement</li>
                                    <li>Encourage kids to speak the animal name while matching</li>
                                    <li>Include fun facts about each animal to spark curiosity</li>
                                </ul>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Match the Animals</h1>

                            <p>
                                Make animal learning fun with this interactive <strong>UKG-level image matching worksheet</strong>. Children will match the names of animals with their corresponding pictures, helping them to develop recognition, vocabulary, and cognitive skills.
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Recognize animals by their appearance</li>
                                <li>Improve visual memory and word association</li>
                                <li>Strengthen early science and general awareness concepts</li>
                            </ul>

                            <h2>🧠 What&apos;s Included?</h2>
                            <p>
                                The worksheet features colorful images of common animals like <em>elephant, lion, cat, dog, cow, monkey, tiger, and zebra</em>. Students will match each picture with the correct animal name from a given list.
                            </p>


                            <h2>🧩 Example Activity</h2>
                            <ul>
                                <li>Image of 🐘 → Elephant</li>
                                <li>Image of 🐯 → Tiger</li>
                                <li>Image of 🐶 → Dog</li>
                            </ul>

                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Sense Organs</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/general-awareness/my-home"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>My Home</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/general-awareness/my-school"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>My School</IonLabel>
                                </IonItem>
                            </div>

                            <h2>👩‍🏫 Teaching Tips</h2>
                            <ul>
                                <li>Use flashcards or real-life toys for reinforcement</li>
                                <li>Encourage kids to speak the animal name while matching</li>
                                <li>Include fun facts about each animal to spark curiosity</li>
                            </ul>
                        </article>
                        <div ref={containerRef} className="contentPart additionWord matchImage match-container" style={{ position: "relative", marginTop: "0%", paddingTop: "0%", height: "calc(-200px + 100vh)" }}>
                            {/* SVG for drawing lines */}
                            <svg ref={svgRef} className="connector-svg" height={height} style={{ position: "absolute", top: 0, left: 0, width: "100%", pointerEvents: "none", zIndex: 10 }}>
                                {connections.map((conn, index) => (
                                    <line
                                        key={index}
                                        x1={conn.x1}
                                        y1={conn.y1}
                                        x2={conn.x2}
                                        y2={conn.y2}
                                        stroke={conn.correct ? "green" : "red"} // Green if correct, Red if incorrect
                                        strokeWidth="3"
                                    />
                                ))}
                            </svg>


                            {/* <h6>{question}</h6> */}
                            {/* Left & Right columns under SVG */}
                            <div className="mb-2 columns" style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 5, width: "100%" }}>
                                <div className="left-column">
                                    {questions.map((item) => (
                                        <div
                                            key={item.left_id}
                                            className={`image-box ${selectedLeft?.id === item.left_id ? "selected" : ""}`}
                                            onClick={(e) => handleLeftClick(e, item.left_id)}
                                            style={{ cursor: "pointer", padding: "10px" }}
                                        >
                                            <img className="img-thumbnail" src={process.env.REACT_APP_API_URL + item.leftImage} alt="Left Option" style={{ width: "100px", height: "100px" }} />
                                        </div>
                                    ))}
                                </div>

                                <div className="right-column">
                                    {questions.map((item) => (
                                        <div key={item.left_id} className="image-box" onClick={(e) => handleRightClick(e, item.right_id)} style={{ cursor: "pointer", padding: "10px" }}>
                                            <img className="img-thumbnail" src={process.env.REACT_APP_API_URL + item.rightImage} alt="Right Option" style={{ width: "100px", height: "100px" }} />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="text-center" style={{ display: 'flex', gap: '10px' }}>
                                <div className="optionButton background-blue" style={{ fontSize: '18px' }} onClick={resetConnections}>
                                    Reset
                                </div>
                                <div className="btn hero-btn" style={{ fontSize: '18px' }} onClick={() => nextQuestion()}>Next &gt;&gt;</div>
                            </div>

                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_LOADING_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default MatchAnimal;
